import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { pluralize } from 'src/utils/pluralize';
import env from 'env';
import { apolloCache as cache } from 'src/graphql';
import { BillingDocument, type IBillingQuery } from 'src/graphql/actions/billing/billing.query.generated';
import { useCancellationPlan } from 'src/context/billing';
import { AppAvatar } from 'src/widgets/AppCard';
import { Card } from 'src/components/ui/card';

import type { IBlockingApp } from 'src/slices/cancellationPlanSlice';

import CallToActionBanner from './CallToActionBanner';
import GetOfferButton from './GetOfferButton';

const AppCard = ({ id, name, logoUrl, unclaimed, activeProtectionsCount }: IBlockingApp) => (
  <Card className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4">
    <div className="flex items-start gap-4 min-w-0">
      <AppAvatar
        appId={id}
        name={name ?? null}
        claimed={!unclaimed}
        logoUrl={logoUrl ?? undefined}
        shape="rounded-square"
        size="md"
      />
      <div className="text-sm min-w-0 space-y-0.5">
        <div className="font-bold truncate">{name || 'Untitled application'}</div>
        <p className="text-muted-foreground">
          {pluralize(activeProtectionsCount, 'active protection', 'active protections')}.
        </p>
      </div>
    </div>
    <div className="shrink-0 [&>a]:w-full sm:[&>a]:w-auto">
      <Button variant="outline" size="sm" asChild>
        <Link to={`/applications/${id}`} className="text-foreground!">
          Terminate in application <ArrowRight />
        </Link>
      </Button>
    </div>
  </Card>
);

const ReviewAccountUsage = () => {
  const {
    ui: { blockingApps },
  } = useCancellationPlan();
  const dataBilling = cache.readQuery<IBillingQuery>({
    query: BillingDocument,
  });

  return (
    <div className="overflow-y-auto space-y-4 -mx-6 px-6">
      <div className="space-y-3">
        {blockingApps.map((app) => (
          <AppCard key={app.id} {...app} />
        ))}
        {Boolean(dataBilling?.billing?.policy.requestExclusiveOffer) && (
          <CallToActionBanner
            title="Keep your protection at a lower price"
            description="To thank you for your business, we're offering you an exclusive loyalty discount. Continue protecting your software with all current features at a reduced rate."
            action={<GetOfferButton />}
          />
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Need some help? If you’re having trouble with Codekeeper application,{' '}
        <a
          href={`mailto:${env.CODEKEEPER_SERVICE_EMAIL}`}
          className="text-accent-software-escrow underline hover:no-underline"
        >
          please get in touch
        </a>
        .
      </p>
    </div>
  );
};

export default ReviewAccountUsage;
